import { CurrencyPipe, DatePipe } from '@angular/common';
import { Column, GridRow } from '@shared';
import { IChangeset } from '@shared-data';
import { IPricingItem } from './pricing-item.interface';

class BundleChangeset implements IChangeset, IPricingItem {
  id: string;
  restaurantId: string;
  status?: string;

  // Bundle Name, Component, Type, Component Name and Brink ID filter with the
  // checkbox list of app-custom-filter, declared as a filterTemplate on
  // bundle-changeset.component.html, the same way LTO, Combos, Kid Meal,
  // Upcharge and Substitution do. Its values are read from the server with
  // distinctItems, so they are not limited to the rows of the current page
  @Column({
    field: 'comboName',
    title: 'Bundle Name',
    order: 1,
    width: 180,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  comboName: string;

  @Column({
    field: 'componentType',
    title: 'Component',
    order: 2,
    width: 120,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  componentType: string;

  isDefault: boolean;

  // Role of the component inside its group. The default is the one included in
  // the bundle, anything else is a substitution the guest pays an upcharge for
  @Column({
    field: 'componentRole',
    title: 'Type',
    order: 3,
    width: 100,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  componentRole: string;

  @Column({
    field: 'componentName',
    title: 'Component Name',
    order: 4,
    width: 200,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  componentName: string;

  @Column({
    field: 'parItemId',
    title: 'Brink ID',
    order: 5,
    width: 80,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
  })
  parItemId?: string;

  // Price charged for the component inside this bundle. On most components that
  // is an override of the bundle and not the price of the item on the menu,
  // which is kept on menuPrice to be shown as a reference
  @Column({
    field: 'itemPrice',
    title: 'Price',
    order: 6,
    width: 80,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
  })
  itemPrice?: number;

  @Column({
    field: 'deliveryPrice',
    title: 'Delivery Price',
    order: 7,
    width: 80,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
  })
  deliveryPrice?: number;

  @Column({
    field: 'comboActive',
    title: 'Combo Active',
    order: 8,
    width: 90,
    filterable: true,
    filterType: 'boolean',
    type: 'boolean',
    sortable: true,
    filterOptions: [
      { name: 'Yes', value: true },
      { name: 'No', value: false },
    ],
  })
  comboActive: boolean;

  @Column({
    field: 'componentActive',
    title: 'Component Active',
    order: 9,
    width: 100,
    filterable: true,
    filterType: 'boolean',
    type: 'boolean',
    sortable: true,
    filterOptions: [
      { name: 'Yes', value: true },
      { name: 'No', value: false },
    ],
  })
  componentActive: boolean;

  // Price of the same product sold on its own. A substitution is priced by the
  // difference against the a la carte price of the default of its group, so the
  // column is what tells the user how much to charge for the swap
  @Column({
    field: 'alcPrice',
    title: 'A La Carte Price',
    order: 10,
    width: 80,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
  })
  alcPrice?: number;

  @Column({
    field: 'alcDeliveryPrice',
    title: 'A La Carte Delivery Price',
    order: 11,
    width: 110,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
  })
  alcDeliveryPrice?: number;

  // Price of the bundle itself. It repeats on every row of the same bundle,
  // because the function returns its components and the bundle is priced as a
  // whole by its own restaurant item
  @Column({
    field: 'bundlePrice',
    title: 'Bundle Price',
    order: 12,
    width: 80,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
  })
  bundlePrice?: number;

  @Column({
    field: 'newPrice',
    title: 'New Price',
    order: 13,
    width: 80,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    editable: true,
    visibleInQuickView: true,
  })
  newPrice?: number;

  @Column({
    field: 'newDeliveryPrice',
    title: 'New Delivery Price',
    order: 14,
    width: 100,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    editable: true,
    visibleInQuickView: true,
  })
  newDeliveryPrice?: number;

  @Column({
    field: 'menuPrice',
    hidden: true,
    title: 'Menu Price',
    order: 15,
    width: 80,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    sortable: true,
    visibleInQuickView: true,
  })
  menuPrice?: number;

  @Column({
    field: 'pricingScheduledDate',
    hidden: true,
    title: 'Price Change Scheduled Date',
    order: 16,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  pricingScheduledDate: Date | null;

  availabilityScheduledDate: Date | null;

  // Identifiers not shown in the grid.
  // comboParId is the value the Bundle sub-selector filters by, and componentId
  // is the pmp.restaurant_items id the new prices are saved against.
  comboParId?: number;
  comboId?: number;
  componentId?: number;
  componentParId?: number;
  componentTypeId?: number;
  componentGroupId?: number | null;

  // True on the row that represents the bundle itself instead of one of its
  // components. That row carries the price of the bundle and its component type
  // reads 'Bundle'.
  isBundle: boolean = false;

  sortOrder?: number;

  // True when the price of the row is an override of this bundle instead of the
  // price of the item on the menu. The new price is then saved as a pending
  // override rather than as a pending price of the item
  isOverridePrice: boolean = false;

  menuDeliveryPrice?: number;

  isDeliveryAvailable?: boolean;

  type: string = 'pricing';

  constructor(changeset?: Partial<BundleChangeset>) {
    this.id = changeset?.id || '';
    this.restaurantId = changeset?.restaurantId || '';
    this.comboName = changeset?.comboName || '';
    this.comboActive = changeset?.comboActive || false;
    // component_type is free text in the database and some values carry
    // trailing spaces, which would break grouping and filtering by value
    this.componentType = changeset?.componentType?.trim() || '';
    this.componentName = changeset?.componentName || '';
    this.parItemId = changeset?.parItemId;
    this.componentActive = changeset?.componentActive || false;
    this.isBundle = changeset?.isBundle || false;
    this.isDefault = changeset?.isDefault || false;
    this.componentRole =
      changeset?.componentRole || (this.isDefault ? 'Default' : 'Substitution');
    this.itemPrice = changeset?.itemPrice;
    this.deliveryPrice = changeset?.deliveryPrice;
    this.menuPrice = changeset?.menuPrice;
    this.bundlePrice = changeset?.bundlePrice;
    this.menuDeliveryPrice = changeset?.menuDeliveryPrice;
    this.isOverridePrice = changeset?.isOverridePrice || false;
    this.newPrice = changeset?.newPrice;
    this.newDeliveryPrice = changeset?.newDeliveryPrice;
    this.pricingScheduledDate = changeset?.pricingScheduledDate
      ? new Date(changeset.pricingScheduledDate)
      : null;
    this.availabilityScheduledDate = changeset?.availabilityScheduledDate
      ? new Date(changeset.availabilityScheduledDate)
      : null;
    this.comboParId = changeset?.comboParId;
    this.comboId = changeset?.comboId;
    this.componentId = changeset?.componentId;
    this.componentParId = changeset?.componentParId;
    this.componentTypeId = changeset?.componentTypeId;
    this.componentGroupId = changeset?.componentGroupId ?? null;
    this.alcPrice = changeset?.alcPrice;
    this.alcDeliveryPrice = changeset?.alcDeliveryPrice;
    this.isDeliveryAvailable = changeset?.isDeliveryAvailable;
    this.sortOrder = changeset?.sortOrder;
    this.status = changeset?.status;
  }
}

export class GridRowBundleChangeset extends GridRow(BundleChangeset) {}
